import React, { JSX, useCallback, useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { Trans } from 'react-i18next';

import {
  getInventoryRulesPublisherSettings,
  InventoryRuleTimezoneType,
  updateInventoryRulesPublisherSettings,
} from '~/src/dash/apiV2/inventory-rules/inventoryRules';
import { highlightedDivStyle } from '~/src/dash/components/layout/common';
import {
  ButtonV2,
  Col,
  Cols,
  fontSizing,
  IndependentSelectV2,
  Panel,
  PanelBreak,
  Panels,
  Row,
  Rows,
  spacing,
  Span,
  Text,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { TimezoneTypeOptions } from '~/src/dash/views/inventory-rules-v2/common';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

export const Settings = (): JSX.Element => {
  const { session } = useBaseStore();
  const { t } = usePublicaTranslation();

  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [timezoneType, setTimezoneType] = useState<Set<InventoryRuleTimezoneType>>(
    new Set([InventoryRuleTimezoneType.UNKNOWN]),
  );

  const [isSaving, setIsSaving] = useState(false);

  // snackbar
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);

  const fetchPublisherSettings = useCallback(() => {
    setLoadState(LoadingStateLoading);
    getInventoryRulesPublisherSettings(session.publisher.ID)
      .then((timezoneType) => {
        unstable_batchedUpdates(() => {
          setTimezoneType(new Set([timezoneType]));
          setLoadState(LoadingStateHasData);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setSnackbarMessage(t('SnackbarErrorRetrievingData'));
          setSnackbarError(true);
          setLoadState(LoadingStateNoData);
        });
      });
  }, []);

  const updatePublisherSettings = useCallback(() => {
    setIsSaving(true);
    updateInventoryRulesPublisherSettings(
      session.publisher.ID,
      timezoneType.values().next().value as InventoryRuleTimezoneType,
    )
      .then((timezoneType) => {
        unstable_batchedUpdates(() => {
          setTimezoneType(new Set([timezoneType]));
          setIsSaving(false);
          setSnackbarMessage(t('SnackbarItemUpdatedSuccessfully', { item: t('TimezoneSetting') }));
          setSnackbarError(false);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setSnackbarMessage(t('SnackbarFailedToUpdateItem', { item: t('TimezoneSetting') }));
          setSnackbarError(true);
          setIsSaving(false);
        });
      });
  }, [timezoneType]);

  useEffect(() => {
    fetchPublisherSettings();
  }, []);

  return (
    <Panels
      max
      vertical
      relative
      largeHorizontalPadding
      verticalPadding
      style={{ rowGap: spacing.medium }}
    >
      <Panel smallVerticalPadding>
        <Rows style={highlightedDivStyle} horizontalPadding smallVerticalPadding gap={spacing.base}>
          <Row noPadding>
            <Cols
              gap={spacing.large}
              style={{ justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Col>
                <Rows>
                  <Row noPadding>
                    <Text bold tKey='TimezoneSetting' />
                  </Row>
                  <Row noPadding>
                    <Text extraSmall>
                      <Trans
                        i18nKey='InventoryRulesTimezoneSettingDescription'
                        components={{ asterisk: <span style={{ color: 'red' }} /> }}
                      />
                    </Text>
                  </Row>
                </Rows>
              </Col>
              <Col>
                <ButtonV2 small onClick={updatePublisherSettings} disabled={isSaving}>
                  {t('Save')}
                </ButtonV2>
              </Col>
            </Cols>
          </Row>
          {loadState !== LoadingStateLoading &&
            !timezoneType.has(InventoryRuleTimezoneType.UNKNOWN) && (
              <Row noPadding>
                <Cols gap={spacing.large} style={{ alignItems: 'center' }}>
                  <Col style={{ minWidth: '160px' }}>
                    <span
                      style={{ display: 'flex', alignItems: 'center', gap: spacing.extraSmall }}
                    >
                      <Text small tKey='Timezone' />
                      <Span color={publicaColors.red} style={{ fontSize: fontSizing.small }}>
                        *
                      </Span>
                    </span>
                  </Col>
                  <Col displayFlex>
                    <IndependentSelectV2
                      key='timezone-setting-select'
                      selectKey=''
                      labelTKey='Timezone'
                      options={TimezoneTypeOptions}
                      selectedOptionIDs={timezoneType}
                      selectedLimit={1}
                      handleApplySelect={(selectedOptionIDs: Set<number | string>) => {
                        setTimezoneType(selectedOptionIDs as Set<InventoryRuleTimezoneType>);
                      }}
                      syncWithParent
                      isSelectionRequired={true}
                      disableSearch={true}
                      disableSort={true}
                      isDisabled={isSaving}
                    />
                  </Col>
                </Cols>
              </Row>
            )}
        </Rows>
      </Panel>
      <SnackbarView
        open={!!snackbarMessage}
        error={!!snackbarError}
        onClose={() => {
          setSnackbarMessage('');
          setSnackbarError(false); // REMOVE THIS
        }}
      >
        {snackbarMessage}
      </SnackbarView>
      <PanelBreak height={spacing.large} />
    </Panels>
  );
};
